




















































































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import { formatDigitsGrouping } from '@/modules/budget/monitoring/reports-constructor/common/utils/formattingUtils';
import { IApp21ExpenseRow, IApp21PprIndicatorRow, IApp21PprItem } from './app21InputForm';
import {
    blockNegativeFactKey,
    blockNegativeFactPaste,
    INDICATOR_PRECISION,
    IReasonOption,
    IRowValue,
    isReasonMissing,
    MONEY_PRECISION,
    parseFact,
    reasonKey,
    rowValues,
} from './app21Utils';

const I18N_PREFIX = 'modules.budget_execution_monitoring.reports.app21';
const NUMBERING_COLORS = ['green', 'red', 'purple', 'orange', 'blue'];

@Component({
    filters: {
        formatNumber(value: unknown): unknown {
            if (typeof value !== 'number' || !Number.isFinite(value)) {
                return value;
            }
            return formatDigitsGrouping(value.toFixed(1)).replace('.', ',');
        },
        // ППР: без принудительного знака после запятой (4, -1), с округлением погрешности float
        formatPlain(value: unknown): unknown {
            if (typeof value !== 'number' || !Number.isFinite(value)) {
                return value;
            }
            const rounded = Math.round(value * 1e6) / 1e6;
            return formatDigitsGrouping(rounded).replace('.', ',');
        }
    }
})
export default class ApplicationForm21Indicators extends Vue {
    // выбранная подпрограмма; ppr = null — БП без подпрограмм
    @Prop({ default: null }) private pprItem!: IApp21PprItem | null;
    // строки budget_execution_application21 формы по всем ППР
    @Prop({ default: () => [] }) private expenses!: IApp21ExpenseRow[];
    // строки budget_execution_application21_ppr по всем ППР
    @Prop({ default: () => [] }) private indicators!: IApp21PprIndicatorRow[];
    @Prop({ default: () => [] }) private reasonOptions!: IReasonOption[];
    // все причины — для показа выбранной, даже если она перестала действовать
    @Prop({ default: () => [] }) private allReasonOptions!: IReasonOption[];
    @Prop({ default: false }) private editAccess!: boolean;
    // строки с ошибкой при последнем неудачном сохранении (ключ — reasonKey)
    @Prop({ default: () => ({}) }) private highlighted!: Record<string, boolean>;

    private readonly i18nPrefix = I18N_PREFIX;


    // строка раздела 1: запись выбранной ППР; для БП без подпрограмм — запись ppr = null
    private get expenseRow(): IApp21ExpenseRow | null {
        if (!this.pprItem) return null;
        const ppr = this.pprItem.ppr;
        return this.expenses.find(x => x.ppr === ppr) ?? null;
    }

    // наименование из dict_ebk_func (type = 5); без подпрограмм — «Без подпрограммы»
    private get pprName(): string {
        if (!this.pprItem) return '';
        if (this.pprItem.ppr === null) {
            return this.$t(`${I18N_PREFIX}.without_ppr`) as string;
        }
        const { name_ru, name_kk } = this.pprItem;
        return (this.$i18n.locale === 'kk' ? name_kk || name_ru : name_ru) || '';
    }

    // ед. изм. фиксированная — «тысяч тенге» / «мың теңге», не из справочника
    private get expenseValues(): IRowValue[] {
        return rowValues(
            this.pprName,
            this.$t(`${I18N_PREFIX}.thousand_tenge`) as string,
            this.expenseRow?.plan ?? null,
            this.expenseRow?.fact ?? null,
        );
    }

    private reasonOption(reasonId: number | null): IReasonOption | null {
        if (reasonId === null) return null;
        return this.allReasonOptions.find(x => x.key === reasonId) ?? null;
    }

    private get selectedReason(): IReasonOption | null {
        return this.reasonOption(this.expenseRow?.reason_id ?? null);
    }

    // подсветка — только у строк с ошибкой при последнем сохранении и пока ошибка не исправлена;
    // у БП без подпрограмм строка ppr = null общая с «Расходами» — подсвечивается на обеих вкладках
    private get isReasonHighlighted(): boolean {
        const row = this.expenseRow;
        return !!row
            && !!this.highlighted[reasonKey('expenses', row.id)]
            && isReasonMissing(row.plan, row.fact, row.reason_id, MONEY_PRECISION);
    }

    // причина подпрограммы; у БП без подпрограмм родитель синхронизирует её с «Расходами»
    private onReasonInput(option: IReasonOption | null) {
        if (!this.expenseRow) return;
        this.$emit('expense-reason-change', this.expenseRow, option?.key ?? null);
    }

    // раздел 2: ППР выбранной подпрограммы, сгруппированные по БИП (строки без БИП — первыми, без заголовка)
    private get indicatorGroups(): {
        bipCode: string | null;
        bipLabel: string;
        rows: { item: IApp21PprIndicatorRow; values: IRowValue[] }[];
    }[] {
        if (!this.pprItem) return [];
        const ppr = this.pprItem.ppr;
        const groups: {
            bipCode: string | null;
            bipLabel: string;
            rows: { item: IApp21PprIndicatorRow; values: IRowValue[] }[];
        }[] = [];

        // строки с бэка уже отсортированы по bip_code NULLS FIRST
        for (const item of this.indicators) {
            if (item.ppr !== ppr) continue;
            let group = groups.find(g => g.bipCode === item.bip_code);
            if (!group) {
                const bipName = this.localized(item.bip_name_ru, item.bip_name_kk);
                group = {
                    bipCode: item.bip_code,
                    // «BIP-XXXXX – Наименование БИП»
                    bipLabel: item.bip_code && bipName ? `${item.bip_code} – ${bipName}` : (item.bip_code ?? ''),
                    rows: [],
                };
                groups.push(group);
            }
            group.rows.push({
                item,
                values: rowValues(
                    this.localized(item.indicator_name_ru, item.indicator_name_kk) || String(item.indicator_id),
                    this.localized(item.unit_name_ru, item.unit_name_kk) || item.unit_code || '-',
                    item.plan,
                    item.fact,
                ),
            });
        }
        return groups;
    }

    // значение по текущему языку; en и пустой kk — на ru
    private localized(ru: string | null, kk: string | null): string {
        return (this.$i18n.locale === 'kk' ? kk || ru : ru) || '';
    }

    private indicatorReason(item: IApp21PprIndicatorRow): IReasonOption | null {
        return this.reasonOption(item.reason_id);
    }

    private isIndicatorReasonHighlighted(item: IApp21PprIndicatorRow): boolean {
        return !!this.highlighted[reasonKey('ppr', item.id)]
            && isReasonMissing(item.plan, item.fact, item.reason_id, INDICATOR_PRECISION);
    }

    // пустое поле = факт не введён (null), отклонение и % не считаются
    private onIndicatorFactInput(item: IApp21PprIndicatorRow, value: string) {
        this.$emit('fact-change', item, parseFact(value));
    }

    // отрицательный факт не вводится
    private blockNegativeFactKey(event: KeyboardEvent) {
        blockNegativeFactKey(event);
    }

    private blockNegativeFactPaste(event: ClipboardEvent) {
        blockNegativeFactPaste(event);
    }

    private onIndicatorReasonInput(item: IApp21PprIndicatorRow, option: IReasonOption | null) {
        this.$emit('reason-change', item, option?.key ?? null);
    }

    private get tableHeaders(): { value: string; key: string; width: string }[] {
        const columns: { key: string; width: string }[] = [
            { key: 'name',      width: '29%' },
            { key: 'unit',      width: '10%' },
            { key: 'plan',      width: '10%' },
            { key: 'fact',      width: '10%' },
            { key: 'deviation', width: '10%' },
            { key: 'percent',   width: '15.5%' },
            { key: 'reason',    width: '15.5%' },
        ];
        return columns.map(col => ({
            ...col,
            value: this.$t(`${I18N_PREFIX}.${col.key}`) as string,
        }));
    }

    // цвет ячейки 
    private numberingColor(idx: number): string {
        return NUMBERING_COLORS[idx % NUMBERING_COLORS.length];
    }
}

